import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

// Iter163 — SEO gate para query params "de ruído".
//
// O Google Search Console reportou centenas de páginas ficando fora
// do índice com os rótulos "Página alternativa com tag canônica
// adequada", "Cópia — Google e o usuário selecionaram uma página
// canônica diferente" e "Erro soft 404". A análise dos URLs afetados
// mostrou um padrão único: quase todos têm query params usados para
// FILTRAR estado de UI, não para gerar conteúdo novo (ex:
// ``/blog?tag=xxx`` mostra o MESMO layout de blog, só que filtrado).
// O crawler indexava cada permutação como página distinta.
//
// Este componente centraliza o tratamento:
//   1. Detecta path + query param específico via ``NOISE_MATCHERS``.
//   2. Injeta ``<meta name="robots" content="noindex,follow">`` para
//      as combinações onde o filtro é puramente UI (Google ignora).
//   3. Emite ``<link rel="canonical">`` apontando para a URL LIMPA
//      (sem query params, ou com path substituído por rota canônica).
//   4. Fica INERTE nas demais rotas — não colide com o SEO próprio
//      da página.
//
// Executa em toda troca de rota (via ``useLocation``), então funciona
// para SPA sem SSR — o Googlebot renderiza JS antes de indexar.

const SITE_URL = "https://juristube.com.br";

// Cada entrada: [regex de path, param que dispara o noindex, função
// que devolve a URL canônica limpa (relativa ao site).]
// A ordem importa — o primeiro match ganha.
const NOISE_MATCHERS = [
  // /blog?tag=X → o /blog principal já tem todos os posts; a versão
  // filtrada por tag é UI só.
  {
    path: /^\/blog\/?$/i,
    param: "tag",
    canonical: () => "/blog",
  },
  // /colunistas?author=X → duplica /autor/{slug} (rota canônica).
  {
    path: /^\/colunistas\/?$/i,
    param: "author",
    canonical: (v) => (v ? `/autor/${v}` : "/colunistas"),
  },
  // /temas?cat=X → filtro de categoria; canonical vai para /temas.
  {
    path: /^\/temas\/?$/i,
    param: "cat",
    canonical: () => "/temas",
  },
  // /busca?q=X → resultados de busca são gerados on-demand, não
  // devem entrar no índice (soft 404 quando não há resultados).
  {
    path: /^\/busca\/?$/i,
    param: "q",
    canonical: () => "/busca",
  },
  {
    path: /^\/busca\/?$/i,
    param: "tab",
    canonical: () => "/busca",
  },
  // /tv?colecao=X → filtro de coleção na TV.
  {
    path: /^\/tv\/?$/i,
    param: "colecao",
    canonical: () => "/tv",
  },
  // /conta?claim=X → workflow interno de reivindicação de perfil,
  // não é conteúdo público.
  {
    path: /^\/conta\/?$/i,
    param: "claim",
    canonical: () => "/conta",
  },
  // Iter307.4 — Qualquer path com o parâmetro `_wsod_recovery` é um
  // fallback interno de SPA (ChunkLoadError → hard-reload). O Google
  // indexou várias variantes (?_wsod_recovery=1712345). Sempre
  // noindex + canonical para o path sem o query param.
  {
    path: /.*/i,
    param: "_wsod_recovery",
    canonical: () => "/",
  },
];

// Rotas onde queremos APENAS canonicalizar (sem noindex) — o
// conteúdo é o mesmo com/sem o param, mas queremos que o Google
// consolide sinais numa URL só.
const CANONICAL_ONLY_MATCHERS = [
  // /radar-constitucional/{slug}?aba=X → ``aba`` é seletor de aba
  // (Anotações / Doutrina / etc.). Todas mostram o mesmo artigo,
  // então canonicalizamos para a URL SEM o param.
  {
    path: /^\/radar-constitucional\/[^/]+\/?$/i,
    param: "aba",
  },
  // /noticias-legislativas?aba=X (Iter170) — Radar Normativo com
  // Tabs. ``aba=diario|biblioteca`` só troca o painel visível,
  // conteúdo é o mesmo. Canonical vai para /noticias-legislativas
  // (sem aba) — evita que o Google indexe as 2 permutações.
  {
    path: /^\/noticias-legislativas\/?$/i,
    param: "aba",
  },
  // /divulgar, /autor/x/divulgar — página do "compartilhador"
  // sempre gera com ``ref=`` do usuário; consolidar para URL base.
  {
    path: /.*/i,
    param: "ref",
  },
  // Query params de rastreamento que aparecem em qualquer URL
  // (utm, fbclid, gclid) sempre canonicalizam sem eles.
  {
    path: /.*/i,
    param: "utm_source",
  },
  {
    path: /.*/i,
    param: "utm_medium",
  },
  {
    path: /.*/i,
    param: "utm_campaign",
  },
  {
    path: /.*/i,
    param: "fbclid",
  },
  {
    path: /.*/i,
    param: "gclid",
  },
  // Iter270 — Paginação / ordenação / filtros duplicados reportados
  // no GSC (Jul/2026). Todos são estado de UI que não altera conteúdo
  // canônico. Consolidamos para a URL base.
  {
    path: /.*/i,
    param: "page",
  },
  {
    path: /.*/i,
    param: "p",
  },
  {
    path: /.*/i,
    param: "sort",
  },
  {
    path: /.*/i,
    param: "filtro",
  },
  {
    path: /.*/i,
    param: "ordem",
  },
];

export default function QueryParamsSEOGate() {
  const location = useLocation();
  const pathname = location.pathname || "/";
  const search = new URLSearchParams(location.search || "");

  // 1) Noise matchers → noindex,follow + canonical limpo.
  for (const m of NOISE_MATCHERS) {
    if (!m.path.test(pathname)) continue;
    const raw = search.get(m.param);
    if (raw === null || raw === undefined) continue;
    // Match. Emite Helmet com robots noindex + canonical limpo.
    const canonicalPath = m.canonical(raw);
    return (
      <Helmet>
        <meta name="robots" content="noindex,follow" />
        <link rel="canonical" href={`${SITE_URL}${canonicalPath}`} />
      </Helmet>
    );
  }

  // 2) Canonical-only matchers → só canonicalizamos SE algum param
  //    ruidoso estiver presente. Não mexemos em robots.
  const hasNoiseParam = CANONICAL_ONLY_MATCHERS.some(
    (m) => m.path.test(pathname) && search.has(m.param),
  );
  if (hasNoiseParam) {
    return (
      <Helmet>
        <link rel="canonical" href={`${SITE_URL}${pathname}`} />
      </Helmet>
    );
  }

  return null;
}
